import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, Input, output, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LandAdmin, LandAdminCmd } from '@app/shared/classes/land';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  // tslint:disable-next-line:component-selector
  selector: '[app-admin-stammdaten-land-entry]',
  templateUrl: './admin-stammdaten-land-entry.component.html',
  imports: [
    NgClass,
    NgStyle,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class AdminStammdatenLandEntryComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly deleted = output();
  readonly reloadEntries = output();

  protected readonly landForm = signal(this.generateForm());
  protected readonly delChosen = signal(false);

  private land: LandAdmin | null = null;

  private generateForm(): UntypedFormGroup {
    return this.formBuilder.group({
      bezeichnungDe  : [this.land?.bezeichnungDe   ?? '', Validators.required],
      bezeichnungFr  : [this.land?.bezeichnungFr   ?? '', Validators.required],
      bezeichnungIt  : [this.land?.bezeichnungIt   ?? '', Validators.required],
      sortierung     : [this.land?.sortierung      ?? '', [Validators.pattern('^[0-9]*$'), Validators.required]],
      zulassungsLand : [this.land?.zulassungsLand  ?? ''],
      importLand     : [this.land?.importLand      ?? ''],
      nutztier       : [this.land?.nutztierErlaubt ?? ''],
      heimtier       : [this.land?.heimtierErlaubt ?? '']
    });
  }

  @Input()
  set set(land: LandAdmin) {
    if (!land) {
      throw new Error('Land must exist');
    }
    this.land = land;
    this.reset();
  }

  protected submit() {
    if (this.land) {
      const formValues = this.landForm().value;
      const cmd = new LandAdminCmd({
        id              : this.land.id,
        bezeichnungDe   : formValues.bezeichnungDe,
        bezeichnungFr   : formValues.bezeichnungFr,
        bezeichnungIt   : formValues.bezeichnungIt,
        sortierung      : formValues.sortierung,
        zulassungsLand  : formValues.zulassungsLand,
        importLand      : formValues.importLand,
        nutztierErlaubt : formValues.nutztier,
        heimtierErlaubt : formValues.heimtier,
      });
      this.backendService.editLand(cmd)
        .subscribe({
          next: result => {
            this.land = result;
            this.reloadEntries.emit();
          },
          error: console.error
        });
    }
  }

  protected reset() {
    this.landForm.set(this.generateForm());
  }

  protected confirmDelete() {
    const land = this.land;
    if (land) {
      this.delChosen.set(true);
      Swal.fire({
        text: this.translateService.instant('admin.land.delete.warning'),
        icon: 'warning',
        buttonsStyling: true,
        showCancelButton: true,
        confirmButtonText: this.translateService.instant('admin.land.delete.yes'),
        cancelButtonText: this.translateService.instant('global.cancel')
      }).then((result) => {
        if (result.value) {
          this.delete(land.id);
        } else {
          this.delChosen.set(false);
        }
      });
    }
  }

  private delete(landId: number) {
    this.backendService.deleteLand(landId)
      .subscribe({
        next: () => {
          this.showAlert(true);
          this.deleted.emit();
        },
        error: error => {
          this.showAlert(false);
          console.error(error.message);
        }
      });
  }

  private showAlert(success: boolean) {
    const msgCode = success
      ? 'admin.land.delete.success'
      : 'admin.land.delete.error';

    Swal.fire({
      text              : this.translateService.instant(msgCode),
      icon              : success ? 'success' : 'error',
      buttonsStyling    : true,
      confirmButtonText : this.translateService.instant('global.ok')
    });
  }
}
